/**
 * @fileoverview Shared badge primitives for grid cells and detail panels — the single
 *             implementation replacing the per-page StatusBadge/TypeBadge duplicates
 *             (datagrid-consistency audit, gap #25).
 * @module ui-admin/components/Badges
 *
 * Responsibilities: - Render small labeled chips with a consistent tone palette.
 *                   - Provide the four standard wrappers (StatusBadge, TypeBadge,
 *                     CategoryBadge, LibraryBadge) so pages stop re-declaring the
 *                     same color maps inline.
 * Dependencies:     - React, lucide-react (optional icons).
 * Architecture:     - Presentation-only. One internal `Badge` core carries the
 *                     styling; the named wrappers are thin tone/label mappings.
 *                     No Dexie, no API, no mock data.
 * Notes:            - `StatusBadge` accepts EITHER `status` (auto-mapped through
 *                     STATUS_VARIANT_MAP + capitalized label) OR explicit
 *                     `label`/`variant` props — making it a drop-in replacement for
 *                     both the tenant-web `components/ui/StatusBadge` API and the
 *                     per-page `{ status }` variants.
 */

import React from 'react';
import { Star, Check, Globe, Lock, Users, EyeOff, type LucideIcon } from 'lucide-react';
import type { SystemLibraryVisibility } from '@firesquirrel/local-db';

/**
 * @contract BadgeVariant
 * Purpose: Semantic tone names shared by all badge wrappers.
 */
export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'purple' | 'orange';

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
  info: 'bg-blue-100 text-blue-700',
  neutral: 'bg-gray-100 text-gray-700',
  purple: 'bg-purple-100 text-purple-700',
  orange: 'bg-orange-100 text-orange-700',
};

/**
 * @contract STATUS_VARIANT_MAP
 * Purpose: Default status-string → variant mapping used by StatusBadge when no
 *          explicit `variant` is supplied. Covers the statuses used across the
 *          grid pages (templates, permits, mail jobs, documents).
 */
export const STATUS_VARIANT_MAP: Record<string, BadgeVariant> = {
  draft: 'neutral',
  pending: 'warning',
  queued: 'info',
  scheduled: 'info',
  processing: 'info',
  published: 'success',
  active: 'success',
  issued: 'success',
  sent: 'success',
  delivered: 'success',
  generated: 'success',
  completed: 'success',
  approved: 'success',
  archived: 'warning',
  expired: 'neutral',
  cancelled: 'neutral',
  rejected: 'warning',
  inactive: 'neutral',
  revoked: 'error',
  failed: 'error',
};

/**
 * @contract BadgeProps
 * Purpose: Props for the base Badge chip.
 * Fields:
 * - label: visible text (also the accessible name).
 * - variant: tone key (default 'neutral').
 * - icon: optional lucide icon rendered left of the label.
 */
export interface BadgeProps {
  label: string;
  variant?: BadgeVariant;
  icon?: LucideIcon;
}

/**
 * @component Badge
 * Purpose:     Base labeled chip — all wrappers render through this.
 * Props:       BadgeProps.
 * Data Source: none (presentation).
 */
export function Badge({ label, variant = 'neutral', icon: Icon }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium ${VARIANT_CLASSES[variant]}`}
    >
      {Icon && <Icon className="h-3 w-3" aria-hidden="true" />}
      {label}
    </span>
  );
}

/**
 * @contract StatusBadgeProps
 * Purpose: Status chip props.
 * Fields:
 * - status: raw status key; mapped through STATUS_VARIANT_MAP and capitalized for
 *   the label. Provide `label`/`variant` to override either side.
 * - label: explicit label override (drop-in for the tenant-web StatusBadge API).
 * - variant: explicit tone override (drop-in for the tenant-web StatusBadge API).
 */
export interface StatusBadgeProps {
  status?: string;
  label?: string;
  variant?: BadgeVariant;
}

/**
 * @component StatusBadge
 * Purpose:     Standard status chip. Auto-maps common status strings to tones;
 *              explicit label/variant props make it a drop-in for both existing
 *              badge APIs found across the grid pages.
 * Props:       StatusBadgeProps.
 * Data Source: none (presentation).
 */
export function StatusBadge({ status, label, variant }: StatusBadgeProps) {
  const resolvedVariant = variant ?? (status ? STATUS_VARIANT_MAP[status.toLowerCase()] ?? 'neutral' : 'neutral');
  const resolvedLabel = label ?? (status ? status.charAt(0).toUpperCase() + status.slice(1) : '');
  return <Badge label={resolvedLabel} variant={resolvedVariant} />;
}

/**
 * @contract TypeBadgeProps
 * Purpose: Type chip props.
 * Fields:
 * - label: visible type text.
 * - variant: tone key (default 'info' — types render blue).
 * - icon: optional lucide icon.
 */
export interface TypeBadgeProps {
  label: string;
  variant?: BadgeVariant;
  icon?: LucideIcon;
}

/**
 * @component TypeBadge
 * Purpose:     Standard record-type chip (e.g. Document / Permit / Email / File).
 * Props:       TypeBadgeProps.
 * Data Source: none (presentation).
 */
export function TypeBadge({ label, variant = 'info', icon }: TypeBadgeProps) {
  return <Badge label={label} variant={variant} icon={icon} />;
}

/**
 * @contract CategoryBadgeProps
 * Purpose: Category chip props.
 * Fields:
 * - label: visible category text.
 * - variant: tone key (default 'purple' — categories render purple).
 * - icon: optional lucide icon.
 */
export interface CategoryBadgeProps {
  label: string;
  variant?: BadgeVariant;
  icon?: LucideIcon;
}

/**
 * @component CategoryBadge
 * Purpose:     Standard category chip (e.g. lookup category, document category).
 * Props:       CategoryBadgeProps.
 * Data Source: none (presentation).
 */
export function CategoryBadge({ label, variant = 'purple', icon }: CategoryBadgeProps) {
  return <Badge label={label} variant={variant} icon={icon} />;
}

/**
 * @contract LibraryBadgeProps
 * Purpose: Library/source chip props (e.g. "Library" vs "Template" provenance).
 * Fields:
 * - label: visible text (default 'Library').
 * - variant: tone key (default 'orange' — library items render orange).
 * - icon: optional lucide icon.
 */
export interface LibraryBadgeProps {
  label?: string;
  variant?: BadgeVariant;
  icon?: LucideIcon;
}

/**
 * @component LibraryBadge
 * Purpose:     Standard library-provenance chip used on template/document grids to
 *              mark rows sourced from a shared library.
 * Props:       LibraryBadgeProps.
 * Data Source: none (presentation).
 */
export function LibraryBadge({ label = 'Library', variant = 'orange', icon }: LibraryBadgeProps) {
  return <Badge label={label} variant={variant} icon={icon} />;
}

/**
 * @contract AssignmentBadgeProps
 * Purpose: Props for the tenant-web dashboard assignment badge.
 * Fields:  isPrimary, isInLibrary, order?
 * Rules:   Renders ★ Primary when `isPrimary` is true, ✓ On area when `isInLibrary && !isPrimary`, and — otherwise.
 * Sync:    N/A (presentation-only).
 */
export interface AssignmentBadgeProps {
  isPrimary?: boolean;
  isInLibrary?: boolean;
  order?: number;
}

/**
 * @component AssignmentBadge
 * Purpose:     Render the dashboard assignment state as a shared badge across `DashboardViewerPage`,
 *              `DashboardTemplates`, and `ReportDefinitionDetailPanel`.
 * Props:       AssignmentBadgeProps.
 * Behavior:    ★ Primary (`isPrimary`), ✓ On area (`isInLibrary && !isPrimary`), — (neither).
 * Data Source: none (presentation-only).
 * Constraints: no Dexie/API calls.
 * Performance: stateless.
 */
export function AssignmentBadge({ isPrimary = false, isInLibrary = false, order }: AssignmentBadgeProps) {
  const suffix = typeof order === 'number' ? ` (#${order})` : '';
  if (isPrimary) {
    return (
      <span
        data-testid="assignment-badge-primary"
        className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700"
      >
        <Star className="h-3 w-3" aria-hidden="true" />
        {`Primary${suffix}`}
      </span>
    );
  }
  if (isInLibrary) {
    return (
      <span
        data-testid="assignment-badge-on-area"
        className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700"
      >
        <Check className="h-3 w-3" aria-hidden="true" />
        {`On area${suffix}`}
      </span>
    );
  }
  return (
    <span
      data-testid="assignment-badge-none"
      title="Not assigned to Dashboard area"
      className="text-xs text-gray-400"
    >
      —
    </span>
  );
}

/**
 * @contract VisibilityBadgeProps
 * Purpose: Props for the system-web System Library visibility badge.
 * Fields:  isInLibrary?, visibility?, tenantCount?
 * Rules:   When `!isInLibrary`, renders "Not in Library". Otherwise renders the visibility level.
 * Sync:    N/A (presentation-only).
 */
export interface VisibilityBadgeProps {
  isInLibrary?: boolean;
  visibility?: SystemLibraryVisibility;
  tenantCount?: number;
}

/**
 * @component VisibilityBadge
 * Purpose:     Render the system library visibility state (`all-departments`, `specific-tenants`,
 *              `system-only`, or `Not in Library`) on `SystemDashboardsPage` and `ReportDefinitionDetailPanel`.
 * Props:       VisibilityBadgeProps.
 * Behavior:    Maps visibility state to icon + tone chip.
 * Data Source: none (presentation-only).
 * Constraints: no Dexie/API calls.
 * Performance: stateless.
 */
export function VisibilityBadge({
  isInLibrary = false,
  visibility = 'all-departments',
  tenantCount = 0,
}: VisibilityBadgeProps) {
  if (!isInLibrary) {
    return (
      <span
        data-testid="visibility-badge-not-in-library"
        className="inline-flex items-center gap-1 text-xs text-gray-400"
      >
        <Lock className="h-3 w-3" aria-hidden="true" />
        Not in Library
      </span>
    );
  }
  if (visibility === 'specific-tenants') {
    return (
      <span
        data-testid="visibility-badge-specific-tenants"
        className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700"
      >
        <Users className="h-3 w-3" aria-hidden="true" />
        {`Specific Tenants (${tenantCount})`}
      </span>
    );
  }
  if (visibility === 'system-only') {
    return (
      <span
        data-testid="visibility-badge-system-only"
        className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700"
      >
        <EyeOff className="h-3 w-3" aria-hidden="true" />
        System Only
      </span>
    );
  }
  return (
    <span
      data-testid="visibility-badge-all-departments"
      className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700"
    >
      <Globe className="h-3 w-3" aria-hidden="true" />
      All Departments
    </span>
  );
}
